/**
 * GroupDrilldown - View for displaying a GROUP_CARD and its children
 *
 * This component handles both:
 * - Dashboard view (root-level GROUP_CARD): /reporting/dashboard/:dashboardId
 * - Nested group view: /reporting/dashboard/:dashboardId/group/:groupId
 *
 * Since dashboards are just root-level GROUP_CARDs, the display logic is identical.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import useTourAnalytics from 'src/hooks/useTourAnalytics';
import { usePostHog } from 'src/lib/posthog';
import { toast } from '@/components/ui/toast';
import {
  CaretDownIcon,
  CaretRightIcon,
  SparkleIcon,
  ShareIcon,
  UsersThreeIcon,
  InfoIcon,
  CalendarCheckIcon,
  ArrowClockwiseIcon,
  WarningIcon,
  KeyIcon,
  DotsThreeIcon,
  ChartLineIcon,
  GaugeIcon
} from '@phosphor-icons/react';
import Joyride from 'react-joyride';
import PageFrame from 'src/components/layouts/PageFrame';
import Panel from 'src/components/ui/Panel';
import SegmentedControl, { type SegmentedControlOption } from 'src/components/ui/SegmentedControl';
import Popover from 'src/components/ui/Popover';
import PeriodPicker from './PeriodPicker';
import JoyrideTooltip from 'src/components/ui/JoyrideTooltip';
import MetricTile from './MetricTile';
import AddNodeCard from './AddNodeCard';
import CardDetailsPanel, { CARD_DETAILS_PANEL_WIDTH, type CardDetailsTab } from './CardDetailsPanel';
import NodeDetailModal from './NodeDetailModal';
import NodeCommentPopup from './NodeCommentPopup';
import DashboardSharing from './DashboardSharing';
import ReportingPager from './ReportingPager';
import AssuranceLevelsInfoModal from './AssuranceLevelsInfoModal';
import DashboardCreateLinks from 'src/features/help/DashboardCreateLinks';
import BulkDataUpdateModal from './BulkDataUpdateModal';
import {
  getDashboardTourSteps,
  dashboardTourStyles,
  dashboardTourLocale,
  handleDashboardTourCallback,
  shouldShowDashboardTour
} from '../dashboardTourConfig';
import { DASHBOARD_TOUR_TARGETS } from '../dashboardTourTargets';
import {
  useDashboardCalculation,
  useGroupSummary,
  useRefreshGroupSummary,
  useMetricNode,
  useCreateMetricNode,
  useUpdateMetricNode,
  useDeleteMetricNode,
  useUpsertLevelBoundaries,
  useDashboards,
  useNodePermissions,
  useCommentCounts,
  queryKeys
} from '../api/hooks';
import AccessOverviewModal from './AccessOverviewModal';
import { useDashboardTileStyle, useReportingUrlState, type DashboardTileStyle } from '../hooks';
import useNodeShare from '../hooks/useNodeShare';
import useAuth from 'src/hooks/useAuth';
import useDockedSidePanel from 'src/hooks/useDockedSidePanel';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { ConfirmModal } from 'src/components/ui/ConfirmModal';
import type { ChartSpec } from 'src/components/charts';
import type { CalculatedMetricNode, MetricNodeType } from '../api/types';
import { ResourcePermissionRole } from '../../chat/types';
import { mapLevelToAssurance, processNodeForDisplay, findNodeById, type ProcessedNode } from '../utils/apiTransforms';
import { dashboardAssuranceColors, type AssuranceLevel } from '../utils/color';
import { DASHBOARD_CHART_TILE_HEIGHT_CLASS, DASHBOARD_CLASSIC_TILE_HEIGHT_CLASS } from '../utils/constants';
import Loading from 'src/components/ui/Loading';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { escapeCurrencyDollars } from 'src/lib/markdownMath';
import Button from 'src/components/ui/Button';
import Dropdown from 'src/components/ui/Dropdown';
import Modal from 'src/components/ui/Modal';
import UnauthorizedPage from 'src/features/auth/UnauthorizedPage';
import {
  METRIC_TILE_SORT_OPTIONS,
  type MetricTileSortOption,
  compareProcessedNodes,
  sortWithPinnedFirst
} from '../utils/sort';
import DashboardChatExampleButtons from './DashboardChatExampleButtons';
import {
  dashboardInsightChatExamples,
  dashboardPopulateChatExamples,
  openDashboardChat,
  type DashboardChatExample
} from '../utils/dashboardChat';
import { lastIsoWeekOfMonth } from '../utils/isoWeek';
import { resolveTenantCode } from 'src/lib/tenantRouting';
import { usePageHeader, type PageHeaderCrumb } from 'src/components/layouts/PageHeader';

const ASSURANCE_LEGEND_LEVELS: Exclude<AssuranceLevel, 'NaN'>[] = [
  'High',
  'Substantial',
  'Moderate',
  'Limited',
  'None'
];

const TREND_WINDOW_OPTIONS = [
  { value: '3', label: 'Last 3', tooltip: 'Last 3 periods at each card’s own cadence (weeks, months, or quarters)' },
  { value: '6', label: 'Last 6', tooltip: 'Last 6 periods at each card’s own cadence (weeks, months, or quarters)' }
] as const;

const TILE_STYLE_OPTIONS = [
  { value: 'chart', label: 'New', icon: ChartLineIcon, tooltip: 'Chart tiles with trend lines' },
  { value: 'classic', label: 'Classic', icon: GaugeIcon, tooltip: 'Classic dial score cards' }
] as const satisfies readonly SegmentedControlOption<DashboardTileStyle>[];

const defaultGetNodePath = (dashboardId: string, nodeId?: string) =>
  nodeId ? `/reporting/dashboard/${dashboardId}/group/${nodeId}` : `/reporting/dashboard/${dashboardId}`;

export interface GroupDrilldownProps {
  dashboardId?: string;
  nestedNodeId?: string;
  getNodePath?: (dashboardId: string, nodeId?: string) => string;
  shell?: 'page' | 'plain';
  preservePeriodOnRoot?: boolean;
  pagerShowReportingHome?: boolean;
  /** Back target on the dashboard root. Default `/reporting`. `null` hides Back. */
  rootParentPath?: string | null;
  onRootDeleted?: () => void;
}

const MONTH_NAMES_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export default function GroupDrilldown({
  dashboardId: dashboardIdProp,
  nestedNodeId,
  getNodePath = defaultGetNodePath,
  shell = 'page',
  preservePeriodOnRoot = true,
  pagerShowReportingHome = true,
  rootParentPath = '/reporting',
  onRootDeleted
}: GroupDrilldownProps = {}) {
  const navigate = useTenantNavigate();
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { pathname } = location;
  const { user } = useAuth();
  const tenantCode = resolveTenantCode(pathname);

  // groupId is optional - if not provided, we're viewing the dashboard (root) itself
  const { dashboardId: routeDashboardId = '', groupId: routeGroupId } = useParams<{
    dashboardId: string;
    groupId?: string;
  }>();
  const dashboardId = dashboardIdProp ?? routeDashboardId;
  // When dashboardId is passed in, ignore the hub's `:groupId` route param.
  const groupId = dashboardIdProp != null ? nestedNodeId : routeGroupId;

  // If no groupId, we're viewing the dashboard itself (root-level GROUP_CARD)
  const isRootView = !groupId;
  const currentNodeId = groupId || dashboardId;

  // Get permission info for the current node (dashboard or nested group)
  const { data: permissionsData } = useNodePermissions(currentNodeId);
  const { data: dashboardsData } = useDashboards();

  // Determine user's permission level
  const dashboardInfo = dashboardsData?.nodes.find((d) => d.id === dashboardId);
  // Prefer permissions endpoint (fetched per-node, always fresh) over dashboard list (may be stale from cache).
  // Fall back to dashboard list permission, then default to READ (safe default).
  // Note: OWNER permission (50) is set when the dashboard is owned by the current user
  const userPermission =
    permissionsData?.current_user_permission ?? dashboardInfo?.user_permission ?? ResourcePermissionRole.READ;
  const isOwner = permissionsData?.is_owner || userPermission === ResourcePermissionRole.OWNER;
  const canShare = isOwner || userPermission >= ResourcePermissionRole.SHARE || permissionsData?.can_share;
  // Access overview replaces the toolbar Share button. Sharing still happens from
  // a row in the overview. While the flag is loading, keep Share in the overflow menu
  // so it does not flash away.
  const accessOverviewEnabled = !!useFeatureFlag('dashboard_access_overview');
  const { tileStyle, setTileStyle } = useDashboardTileStyle();
  const useChartTiles = tileStyle === 'chart';
  // Tag every PostHog event with the active layout so classic usage can be segmented and counted.
  useEffect(() => {
    posthog?.register({ dashboard_tile_style: tileStyle });
  }, [posthog, tileStyle]);
  const canEdit = userPermission >= ResourcePermissionRole.WRITE;
  const canEditData = userPermission >= ResourcePermissionRole.DATA_ENTRY;
  const canComment = userPermission >= ResourcePermissionRole.COMMENT;

  const [details, setDetails] = useState<{
    nodeId: string;
    tab: CardDetailsTab;
    settingsTab?: string;
  } | null>(null);
  // On wide screens the details pane docks beside the dashboard, which shifts left
  // to make room so no tiles are hidden. Narrower screens keep the overlay.
  const {
    docked: detailsDocked,
    shellRef,
    shellStyle
  } = useDockedSidePanel(useChartTiles && !!details, CARD_DETAILS_PANEL_WIDTH);
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  // The classic-tiles surface opens NodeDetailModal directly rather than
  // through `details`, so a ?settings=reminders deep link needs its own slot to
  // travel in.
  const [editingSettingsTab, setEditingSettingsTab] = useState<string | undefined>(undefined);
  const [commentNodeId, setCommentNodeId] = useState<string | null>(null);
  const [commentNodeName, setCommentNodeName] = useState<string>('');
  const [commentNodeType, setCommentNodeType] = useState<ProcessedNode['type'] | null>(null);
  const [commentNodeCadence, setCommentNodeCadence] = useState<ProcessedNode['updateCadence']>(null);
  const [commentAnchorEl, setCommentAnchorEl] = useState<HTMLElement | null>(null);
  const [unlinkingNode, setUnlinkingNode] = useState<{ id: string; name: string } | null>(null);
  const deleteNodeMutation = useDeleteMetricNode();
  const updateNodeMutation = useUpdateMetricNode();

  // Drag and drop state
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null);
  const [, setDragOverLinkedCardId] = useState<string | null>(null);
  const [, setDragOverDataCardId] = useState<string | null>(null);

  // Handle openNode query parameter - used when navigating from email mentions or linked cards
  const permissionsLoaded = !!permissionsData || !!dashboardInfo;
  useEffect(() => {
    if (!permissionsLoaded) return;
    const openNodeId = searchParams.get('openNode');
    if (openNodeId) {
      const settings = searchParams.get('settings');
      const settingsTab = settings === 'reminders' ? 'Reminders' : undefined;
      if (useChartTiles) {
        setDetails({ nodeId: openNodeId, tab: 'comments', settingsTab });
      } else {
        setEditingNodeId(openNodeId);
        setEditingSettingsTab(settingsTab);
      }
      searchParams.delete('openNode');
      searchParams.delete('settings');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, permissionsLoaded, useChartTiles]);

  // If the viewer switches tile style while a surface is open, move
  // the user onto the matching chrome instead of leaving a blank state.
  const prevUseChartTiles = useRef(useChartTiles);
  useEffect(() => {
    if (prevUseChartTiles.current === useChartTiles) return;
    prevUseChartTiles.current = useChartTiles;
    if (useChartTiles && editingNodeId) {
      setDetails({ nodeId: editingNodeId, tab: 'history', settingsTab: editingSettingsTab });
      setEditingNodeId(null);
      setEditingSettingsTab(undefined);
    } else if (!useChartTiles && details) {
      setEditingNodeId(details.nodeId);
      setEditingSettingsTab(details.settingsTab);
      setDetails(null);
    }
    if (useChartTiles) {
      setCommentNodeId(null);
      setCommentNodeName('');
      setCommentNodeType(null);
      setCommentNodeCadence(null);
      setCommentAnchorEl(null);
    }
  }, [useChartTiles, editingNodeId, details, editingSettingsTab]);

  // URL-persisted state for date and trend settings
  const {
    year: selectedYear,
    month: selectedMonth,
    week: selectedWeek,
    grain: selectedGrain,
    trendWindow,
    hasExplicitPeriod,
    setTrendWindow,
    setReportingState,
    buildPath
  } = useReportingUrlState();

  const displayWeek = selectedWeek ?? lastIsoWeekOfMonth(selectedYear, selectedMonth).isoWeek;

  const pathForNode = useCallback(
    (nodeId?: string, preservePeriod = true) => {
      const path = getNodePath(dashboardId, nodeId);
      return preservePeriod ? buildPath(path) : path;
    },
    [buildPath, dashboardId, getNodePath]
  );

  const openDashboardReminderSettings = useCallback(() => {
    // Land on the dashboard itself (not a nested group), then open its
    // Reminders tab — the same deep link used from emails and linked cards.
    const withPeriod = pathForNode(undefined);
    const qIndex = withPeriod.indexOf('?');
    const pathname = qIndex === -1 ? withPeriod : withPeriod.slice(0, qIndex);
    const params = new URLSearchParams(qIndex === -1 ? '' : withPeriod.slice(qIndex + 1));
    params.set('openNode', dashboardId);
    params.set('settings', 'reminders');
    setDetails(null);
    setEditingNodeId(null);
    setEditingSettingsTab(undefined);
    navigate(`${pathname}?${params.toString()}`);
  }, [pathForNode, dashboardId, navigate]);

  // Auto-resolve period: on first load without URL params, let the backend
  // determine the latest month with data instead of defaulting to the current month.
  const periodSynced = useRef(false);
  const [showStaleDataBanner, setShowStaleDataBanner] = useState(false);

  // Reset when navigating to a different dashboard
  useEffect(() => {
    periodSynced.current = false;
    setShowStaleDataBanner(false);
  }, [dashboardId]);

  // When no explicit period in URL and we haven't synced yet, omit period params
  // so the backend resolves to the latest month with data.
  const apiYear = hasExplicitPeriod || periodSynced.current ? selectedYear : undefined;
  const apiMonth = hasExplicitPeriod || periodSynced.current ? selectedMonth : undefined;
  // Always pass the representative week so weekly cards pin to a complete week.
  // Omitting it in month/quarter grain makes those cards roll up a partial
  // period and look incomplete next to monthly/quarterly cards.
  const apiWeek = hasExplicitPeriod || periodSynced.current ? displayWeek : undefined;

  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [showInsightModal, setShowInsightModal] = useState<boolean>(false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [showAccessOverview, setShowAccessOverview] = useState<boolean>(false);
  const [showAssuranceInfoModal, setShowAssuranceInfoModal] = useState<boolean>(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState<boolean>(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const legendAnchorRef = useRef<HTMLSpanElement>(null);
  const [runTour, setRunTour] = useState(false);

  // Dashboard sharing hook (for root-level share button)
  const {
    existingShares,
    groupShares: existingGroupShares,
    tenantShare,
    handleAddPeople,
    handleRemovePerson,
    handleRemoveGroupShare,
    handleUpdatePermission,
    handleShareWithTenant,
    handleUpdateTenantShare,
    handleRemoveTenantShare,
    inheritedTenantPermission
  } = useNodeShare({
    nodeId: canShare ? dashboardId : undefined,
    permission: userPermission
  });

  // Group-level sharing hook (for nested group share button)
  const {
    existingShares: groupSharesList,
    groupShares: groupExistingGroupShares,
    tenantShare: groupTenantShare,
    handleAddPeople: handleGroupAddPeople,
    handleRemovePerson: handleGroupRemovePerson,
    handleRemoveGroupShare: handleGroupRemoveGroupShare,
    handleUpdatePermission: handleGroupUpdatePermission,
    handleShareWithTenant: handleGroupShareWithTenant,
    handleUpdateTenantShare: handleGroupUpdateTenantShare,
    handleRemoveTenantShare: handleGroupRemoveTenantShare,
    inheritedTenantPermission: groupInheritedTenantPermission
  } = useNodeShare({
    nodeId: !isRootView && canShare ? groupId : undefined,
    permission: userPermission
  });
  const [showGroupShareModal, setShowGroupShareModal] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<MetricTileSortOption>('default');

  // Access overview rows navigate here and ask the arriving view to open its
  // own share panel — the same panel the row would open if you were already on
  // that card. Location state is a one-shot so a refresh or shared URL does not
  // reopen it.
  useEffect(() => {
    const state = location.state as { openShareModal?: boolean } | null;
    if (!state?.openShareModal || !permissionsLoaded) return;
    if (isRootView) {
      setShowShareModal(true);
    } else {
      setShowGroupShareModal(true);
    }
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: null }
    );
  }, [location.state, location.pathname, location.search, location.hash, isRootView, navigate, permissionsLoaded]);

  // Reset summary panel when navigating to a different group or dashboard
  // Note: Date/trend settings are preserved in URL for shareability
  const prevNodeId = useRef(currentNodeId);
  useEffect(() => {
    if (prevNodeId.current !== currentNodeId) {
      setShowSummary(false);
      setShowInsightModal(false);
      prevNodeId.current = currentNodeId;
    }
  }, [currentNodeId]);

  const captureTourEvent = useTourAnalytics('assurance_dashboard_tour', { dashboard_id: dashboardId });
  const dashboardTourSteps = useMemo(() => getDashboardTourSteps(useChartTiles), [useChartTiles]);
  const handleTourCallbackWrapper = (data: any) => {
    handleDashboardTourCallback(data, setRunTour);
    captureTourEvent(data);
    if (data.type === 'error:target_not_found') {
      posthog?.capture('assurance_dashboard_tour_target_missing', {
        dashboard_id: dashboardId,
        step_index: data.index,
        target: String(data.step?.target ?? '')
      });
    }
  };

  // Fetch the node metadata (for name)
  const { data: currentNodeData } = useMetricNode(currentNodeId);

  // Fetch calculated dashboard data
  const {
    data: calcData,
    isLoading,
    error,
    refetch
  } = useDashboardCalculation(dashboardId, apiYear, apiMonth, trendWindow - 1, apiWeek, selectedGrain, tileStyle);

  useEffect(() => {
    if (!isRootView || isLoading || !calcData || !canEdit) return;
    if (!shouldShowDashboardTour()) return;
    setRunTour(true);
  }, [isRootView, isLoading, calcData, canEdit]);

  // Sync resolved period from backend response into URL state
  useEffect(() => {
    if (!calcData || hasExplicitPeriod || periodSynced.current) return;

    periodSynced.current = true;
    const resolvedYear = calcData.period_year;
    const resolvedMonth = calcData.period_month;

    // Pre-populate cache for the explicit query key to avoid a redundant refetch
    const explicitKey = queryKeys.dashboardCalculation(
      dashboardId,
      resolvedYear,
      resolvedMonth,
      trendWindow - 1,
      lastIsoWeekOfMonth(resolvedYear, resolvedMonth).isoWeek,
      selectedGrain,
      tileStyle
    );
    queryClient.setQueryData(explicitKey, calcData);

    const now = new Date();
    const isCurrentMonth = resolvedYear === now.getFullYear() && resolvedMonth === now.getMonth() + 1;

    if (!isCurrentMonth) {
      setReportingState({ year: resolvedYear, month: resolvedMonth });
      setShowStaleDataBanner(true);
    }
  }, [calcData, hasExplicitPeriod, setReportingState, dashboardId, trendWindow, queryClient, selectedGrain, tileStyle]);

  // Fetch AI summary for the current node. Chart tiles load it for the hero teaser;
  // classic layout loads it when the expander is opened.
  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    error: summaryError
  } = useGroupSummary(
    currentNodeId,
    apiYear,
    apiMonth,
    trendWindow - 1,
    showSummary || useChartTiles,
    apiWeek,
    selectedGrain
  );

  const refreshSummaryMutation = useRefreshGroupSummary(
    currentNodeId,
    apiYear,
    apiMonth,
    trendWindow - 1,
    apiWeek,
    selectedGrain
  );
  const refreshSummary = refreshSummaryMutation.mutate;

  // Opening the insights for a view that has no summary yet generates the first one.
  // Once one exists, it stays as is until Refresh is clicked, which always generates a new one.
  const autoGeneratedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!summaryData || summaryData.summary !== null) return;
    const key = [currentNodeId, apiYear, apiMonth, apiWeek, selectedGrain, trendWindow].join('|');
    if (autoGeneratedKeyRef.current === key) return;
    autoGeneratedKeyRef.current = key;
    refreshSummary();
  }, [summaryData, currentNodeId, apiYear, apiMonth, apiWeek, selectedGrain, trendWindow, refreshSummary]);

  const hasSummary = !!summaryData?.summary;
  const insightBody =
    isSummaryLoading || (refreshSummaryMutation.isPending && !hasSummary) ? (
      <Loading text="Analysing dashboard trends..." />
    ) : (summaryError || refreshSummaryMutation.isError) && !hasSummary ? (
      <div className="space-y-2">
        <p className="text-sm text-red-600">Failed to generate summary. Please try again.</p>
        <Button variant="outline" size="sm" icon={ArrowClockwiseIcon} onClick={() => refreshSummary()}>
          Retry
        </Button>
      </div>
    ) : summaryData?.summary ? (
      <div className="space-y-3">
        <div className="relative">
          <div
            className={`prose max-w-none leading-6 text-content-default marker:text-content-subtlest w-full min-w-0 ${
              refreshSummaryMutation.isPending ? 'opacity-40' : ''
            }`}
            aria-busy={refreshSummaryMutation.isPending || undefined}
          >
            <ReactMarkdown
              remarkPlugins={[remarkMath, remarkGfm]}
              rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: false }]]}
              components={{
                table: ({ children, ...props }) => (
                  <div className="overflow-x-auto custom-scrollbar">
                    <table {...props}>{children}</table>
                  </div>
                )
              }}
            >
              {escapeCurrencyDollars(summaryData.summary)}
            </ReactMarkdown>
          </div>
          {refreshSummaryMutation.isPending && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/70">
              <div className="sticky top-1/2 -translate-y-1/2">
                <Loading text="Analysing dashboard trends..." />
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="text-xs text-content-subtle">
            Generated {summaryData.generated_at ? new Date(summaryData.generated_at).toLocaleString() : ''}
          </p>
          {!summaryData.is_current && (
            <p className="text-xs text-amber-700">The dashboard has changed since this was generated.</p>
          )}
          {refreshSummaryMutation.isError && <p className="text-xs text-red-600">Refresh failed. Please try again.</p>}
          <Button
            variant="ghost"
            size="sm"
            icon={ArrowClockwiseIcon}
            loading={refreshSummaryMutation.isPending}
            onClick={() => refreshSummary()}
          >
            Refresh
          </Button>
        </div>
      </div>
    ) : (
      <p className="text-sm text-content-subtle italic">No insight available yet.</p>
    );

  const createNodeMutation = useCreateMetricNode();
  const upsertBoundariesMutation = useUpsertLevelBoundaries();

  // Note: selectedYear and selectedMonth come from URL state (useReportingUrlState)
  // for shareability and bookmark support

  // Month names for display
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Get the current group's data
  const { currentGroup, children } = useMemo(() => {
    if (!calcData) {
      return { currentGroup: null, children: [] };
    }

    if (isRootView) {
      // Viewing the dashboard root - children are the direct children from calcData
      const processedChildren = calcData.children.map(processNodeForDisplay);
      return {
        currentGroup: {
          id: dashboardId,
          name: calcData.dashboard_name,
          value: null,
          assurance: mapLevelToAssurance(calcData.assurance_level),
          trend: 'static' as const,
          sparkline: [],
          suffix: '',
          children: processedChildren,
          type: 'group_card' as const,
          previousAssuranceLevel:
            mapLevelToAssurance(calcData.previous_assurance_level) === 'NaN'
              ? null
              : mapLevelToAssurance(calcData.previous_assurance_level),
          historicalAssuranceLevels: (calcData.historical_assurance_levels || []).map((l) => mapLevelToAssurance(l)),
          updateCadence: null,
          chartType: calcData.chart_type,
          aggregationType: null,
          linkedNodeId: null,
          linkedNodeDashboardId: null,
          hasLinkedCardAccess: null,
          groupNumericDisplayValue: calcData.group_numeric_display_value ?? null,
          groupNumericDisplayTooltip: (() => {
            if (calcData.group_numeric_display_value == null) return null;
            switch (calcData.group_numeric_display_type) {
              case 'assurance_score':
                return "Numeric rank of this group's assurance level (1 = None → 5 = High)";
              case 'child_score_aggregate': {
                const aggLabels: Record<string, string> = {
                  average: 'Average',
                  sum: 'Sum',
                  min: 'Minimum',
                  max: 'Maximum'
                };
                const label = aggLabels[calcData.group_numeric_child_aggregation ?? 'average'] ?? 'Aggregate';
                return `${label} of direct child card values`;
              }
              case 'pinned_child': {
                const processedChildren = calcData.children.map(processNodeForDisplay);
                const pinned = processedChildren.find((c) => c.id === calcData.pinned_child_id);
                return pinned ? `Value from: ${pinned.name}` : 'Value from selected child card';
              }
              default:
                return null;
            }
          })(),
          isPinned: false,
          chartSpec: (calcData.chart_spec ?? null) as ChartSpec | null
        },
        children: processedChildren
      };
    } else {
      // Viewing a nested group - find it in the tree
      const allProcessed = calcData.children.map(processNodeForDisplay);
      const found = findNodeById(allProcessed, groupId!);
      return {
        currentGroup: found,
        children: found?.children || []
      };
    }
  }, [calcData, dashboardId, groupId, isRootView]);

  const sortedChildren = useMemo(() => {
    return sortWithPinnedFirst(
      children,
      sortBy,
      (child) => child.isPinned,
      (a, b) => compareProcessedNodes(a, b, sortBy)
    );
  }, [children, sortBy]);

  // Fetch comment counts for all visible child nodes (for badge display)
  const childNodeIds = useMemo(() => children.map((c) => c.id), [children]);
  const allNodeIds = useMemo(
    () => (currentGroup ? [currentGroup.id, ...childNodeIds] : childNodeIds),
    [currentGroup, childNodeIds]
  );
  const commentWeek = selectedGrain === 'week' ? displayWeek : undefined;
  const { data: commentCountsData } = useCommentCounts(
    allNodeIds,
    selectedYear,
    selectedMonth,
    commentWeek,
    selectedGrain,
    allNodeIds.length > 0
  );
  const commentCounts = commentCountsData?.counts ?? {};

  const handleOpenDetails = (nodeId: string, nodeType: ProcessedNode['type']) => {
    setDetails({ nodeId, tab: nodeType === 'group_card' ? 'comments' : 'history' });
  };

  const handleCloseDetails = () => {
    setDetails(null);
  };

  const handleOpenComments = (
    nodeId: string,
    nodeName: string,
    nodeType: ProcessedNode['type'],
    updateCadence: ProcessedNode['updateCadence'],
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    setCommentNodeId(nodeId);
    setCommentNodeName(nodeName);
    setCommentNodeType(nodeType);
    setCommentNodeCadence(updateCadence);
    setCommentAnchorEl(e.currentTarget);
  };

  const handleCloseComments = () => {
    setCommentNodeId(null);
    setCommentNodeName('');
    setCommentNodeType(null);
    setCommentNodeCadence(null);
    setCommentAnchorEl(null);
  };

  const openCreatedCard = (nodeId: string) => {
    if (useChartTiles) {
      setDetails({ nodeId, tab: 'history' });
    } else {
      setEditingNodeId(nodeId);
    }
  };

  const handleTogglePin = (nodeId: string, isPinned: boolean) => {
    updateNodeMutation.mutate(
      { nodeId, data: { is_pinned: !isPinned } },
      {
        onSuccess: () => {
          refetch();
        }
      }
    );
  };

  const handleCreateNode = async (data: {
    title: string;
    type: 'group_card' | 'data_card' | 'status_card' | 'scope';
    parentId?: string | null;
    linked_node_id?: string;
    data_value_type?: 'number' | 'percentage';
    aggregation_type?: 'average' | 'min';
    boundary_direction?: 'descending' | 'ascending';
  }) => {
    if (!dashboardId || data.type === 'scope') return;

    const result = await createNodeMutation.mutateAsync({
      title: data.title,
      type: data.type as MetricNodeType,
      parent_id: currentNodeId, // Current group is the parent
      linked_node_id: data.linked_node_id,
      data_value_type: data.data_value_type,
      aggregation_type: data.aggregation_type,
      boundary_direction: data.boundary_direction
    });

    posthog?.capture('assurance_node_created', {
      dashboard_id: dashboardId,
      node_id: result.id,
      node_name: data.title,
      node_type: data.type,
      parent_id: currentNodeId,
      is_linked: !!data.linked_node_id
    });

    // For data cards (not linked), save default level boundaries immediately so
    // values are persisted even if the user navigates away without clicking Save.
    if (data.type === 'data_card' && !data.linked_node_id) {
      await upsertBoundariesMutation.mutateAsync({
        nodeId: result.id,
        data: {
          boundaries: [
            { level: 'high', threshold_value: 90 },
            { level: 'substantial', threshold_value: 75 },
            { level: 'moderate', threshold_value: 50 },
            { level: 'limited', threshold_value: 25 },
            { level: 'none', threshold_value: null }
          ]
        }
      });
      await refetch();
      openCreatedCard(result.id);
      toast.info('Add data values and configure assurance rules for your new card');
    } else if (data.type === 'status_card' && !data.linked_node_id) {
      // Status cards have no assurance rules to configure, but opening the edit
      // modal lets the user record their first Yes / No toggle immediately.
      await refetch();
      openCreatedCard(result.id);
      toast.info('Record your first Yes / No status for this card');
    } else {
      refetch();
    }
  };

  const handleNodeDeleted = () => {
    const deletedId = details?.nodeId ?? editingNodeId;
    setDetails(null);
    setEditingNodeId(null);
    if (deletedId === currentNodeId && !isRootView) {
      navigate(pathForNode(undefined, preservePeriodOnRoot));
    } else if (deletedId === currentNodeId && isRootView) {
      if (onRootDeleted) {
        onRootDeleted();
      } else {
        navigate('/reporting');
      }
    } else {
      refetch();
    }
  };

  const handleUnlinkConfirm = async () => {
    if (!unlinkingNode) return;

    try {
      await deleteNodeMutation.mutateAsync(unlinkingNode.id);
      posthog?.capture('assurance_linked_card_unlinked', {
        dashboard_id: dashboardId,
        node_id: unlinkingNode.id,
        node_name: unlinkingNode.name
      });
      refetch();
    } finally {
      setUnlinkingNode(null);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (nodeId: string) => {
    setDraggingNodeId(nodeId);
  };

  const handleDragEnd = () => {
    setDraggingNodeId(null);
    setDragOverGroupId(null);
    setDragOverLinkedCardId(null);
    setDragOverDataCardId(null);
  };

  const handleDropCard = async (targetParentId: string) => {
    if (!draggingNodeId) return;

    // Don't allow dropping a card onto itself
    if (draggingNodeId === targetParentId) {
      setDraggingNodeId(null);
      setDragOverGroupId(null);
      toast.error('Cannot move a card into itself');
      return;
    }

    try {
      // Update the card with new parent
      await updateNodeMutation.mutateAsync({
        nodeId: draggingNodeId,
        data: { parent_id: targetParentId }
      });

      // Find the card name and target parent name for the toast
      const draggedCard = children.find((c) => c.id === draggingNodeId);
      const targetIsScope = targetParentId === dashboardId;
      const targetGroup = !targetIsScope ? children.find((c) => c.id === targetParentId) : null;
      const targetName = targetIsScope ? currentNodeData?.title || 'Dashboard' : targetGroup?.name || 'Group';

      toast.success(`Moved "${draggedCard?.name || 'card'}" to "${targetName}"`);

      posthog?.capture('assurance_card_drag_moved', {
        dashboard_id: dashboardId,
        node_id: draggingNodeId,
        target_parent_id: targetParentId,
        from_parent_id: currentNodeId,
        drop_location: targetIsScope ? 'breadcrumb' : 'group_card'
      });

      // Refresh the data
      refetch();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'Failed to move card. Please try again.';
      toast.error(errorMessage);
    } finally {
      setDraggingNodeId(null);
      setDragOverGroupId(null);
    }
  };

  const handleDragOverGroup = (groupId: string) => {
    // Don't allow dragging over self
    if (groupId !== draggingNodeId) {
      setDragOverGroupId(groupId);
    }
  };

  const handleDragLeaveGroup = () => {
    setDragOverGroupId(null);
  };

  const handleDragOverLinkedCard = (cardId: string) => {
    setDragOverLinkedCardId(cardId);
    // Clear the valid drag over state
    setDragOverGroupId(null);
  };

  const handleDragLeaveLinkedCard = () => {
    setDragOverLinkedCardId(null);
  };

  const handleDragOverDataCard = (cardId: string) => {
    setDragOverDataCardId(cardId);
    // Clear the valid drag over state
    setDragOverGroupId(null);
  };

  const handleDragLeaveDataCard = () => {
    setDragOverDataCardId(null);
  };

  // Check for 403/404 errors to show appropriate access denied message
  const axiosError = error as { response?: { status?: number } } | null;
  const errorStatus = axiosError?.response?.status;
  const isAccessDenied = error && (errorStatus === 403 || errorStatus === 404);

  const groupName = currentNodeData?.title || currentGroup?.name || (isRootView ? 'Dashboard' : 'Group');
  const dashboardName = calcData?.dashboard_name || 'Dashboard';
  const populateChatScope = isRootView ? 'dashboard' : 'group';
  const populateChatResourceTitle = isRootView ? dashboardName : groupName;

  const openScopedDashboardChat = useCallback(
    (event: string, example: DashboardChatExample, extra?: Record<string, string>) => {
      posthog?.capture(event, {
        dashboard_id: dashboardId,
        node_id: currentNodeId,
        is_root: isRootView,
        example: example.id,
        ...extra
      });
      openDashboardChat({
        query: example.query,
        tenantCode,
        resourceId: currentNodeId,
        resourceTitle: populateChatResourceTitle,
        returnTo: pathForNode(isRootView ? undefined : groupId)
      });
    },
    [posthog, dashboardId, currentNodeId, isRootView, groupId, populateChatResourceTitle, tenantCode, pathForNode]
  );

  const handlePopulateWithChat = useCallback(
    (example: DashboardChatExample) => {
      openScopedDashboardChat('assurance_populate_chat_opened', example, {
        intent: example.id,
        source: 'empty_state'
      });
    },
    [openScopedDashboardChat]
  );

  const handleInsightChat = useCallback(
    (example: DashboardChatExample) => {
      openScopedDashboardChat('assurance_insight_chat_opened', example);
      setShowInsightModal(false);
    },
    [openScopedDashboardChat]
  );

  const handleOpenShare = useCallback(() => {
    if (isRootView) {
      posthog?.capture('assurance_share_modal_opened', { dashboard_id: dashboardId });
      setShowShareModal(true);
    } else {
      posthog?.capture('assurance_group_share_modal_opened', {
        dashboard_id: dashboardId,
        group_id: groupId
      });
      setShowGroupShareModal(true);
    }
  }, [posthog, dashboardId, isRootView, groupId]);

  const wrapShell = (content: ReactNode, title = `${groupName} | CompliMind`) => {
    const shifted = (
      <div ref={shellRef} className="transition-[padding] duration-200 ease-out" style={shellStyle}>
        {content}
      </div>
    );
    return shell === 'plain' ? (
      shifted
    ) : (
      <PageFrame size="md" title={title}>
        <Panel>{shifted}</Panel>
      </PageFrame>
    );
  };

  // Build the full breadcrumb path from root to current group
  const { groupDepth, intermediatePath, parentPath } = useMemo(() => {
    // At root dashboard view, back goes to /reporting
    if (isRootView) {
      return { groupDepth: 0, intermediatePath: [], parentPath: rootParentPath ?? undefined };
    }

    if (!calcData || !groupId) {
      return { groupDepth: 0, intermediatePath: [], parentPath: undefined };
    }

    // Find path from root to target node
    const findPathToNode = (
      nodes: CalculatedMetricNode[],
      targetId: string,
      currentPath: { id: string; name: string }[] = []
    ): { id: string; name: string }[] | null => {
      for (const node of nodes) {
        if (node.id === targetId) {
          return currentPath;
        }
        if (node.children && node.children.length > 0) {
          const found = findPathToNode(node.children, targetId, [...currentPath, { id: node.id, name: node.title }]);
          if (found) return found;
        }
      }
      return null;
    };

    const path = findPathToNode(calcData.children, groupId) || [];
    const depth = path.length + 1; // +1 for the current group itself

    // Build intermediate breadcrumb items (groups between dashboard and current)
    const intermediate = path.map((item) => ({
      name: item.name,
      path: pathForNode(item.id),
      id: item.id
    }));

    // Parent path for "Up" button - either last intermediate item or the dashboard root
    const parent =
      path.length > 0 ? pathForNode(path[path.length - 1].id) : pathForNode(undefined, preservePeriodOnRoot);

    return {
      groupDepth: depth,
      intermediatePath: intermediate,
      parentPath: parent
    };
  }, [calcData, groupId, isRootView, pathForNode, preservePeriodOnRoot, rootParentPath]);

  const headerCrumbs = useMemo<PageHeaderCrumb[] | null>(() => {
    if (shell !== 'page') return null;
    const crumbs: PageHeaderCrumb[] = [];
    if (pagerShowReportingHome) {
      crumbs.push({ label: 'Dashboards', to: '/reporting' });
    }
    const scopePath = pathForNode(undefined, preservePeriodOnRoot);
    if (isRootView) {
      crumbs.push({ label: dashboardName });
      return crumbs;
    }
    crumbs.push({ label: dashboardName, to: scopePath });
    for (const item of intermediatePath) {
      crumbs.push({ label: item.name, to: item.path });
    }
    crumbs.push({ label: groupName });
    return crumbs;
  }, [
    shell,
    pagerShowReportingHome,
    isRootView,
    dashboardName,
    groupName,
    intermediatePath,
    pathForNode,
    preservePeriodOnRoot
  ]);
  usePageHeader(headerCrumbs);

  // Prepare data cards for bulk update (all descendant data + status cards, not linked)
  const bulkUpdateDataCards = useMemo(() => {
    if (!children) return [];

    // Recursively collect all data + status cards from the tree with their paths
    const collectDataCards = (
      nodes: ProcessedNode[],
      parentPath: string = ''
    ): {
      id: string;
      name: string;
      path: string;
      dataValueType: 'percentage' | 'number';
      currentValue: number | null;
      nodeType: 'data_card' | 'status_card';
      updateCadence: 'monthly' | 'quarterly' | 'weekly' | null;
    }[] => {
      const result: {
        id: string;
        name: string;
        path: string;
        dataValueType: 'percentage' | 'number';
        currentValue: number | null;
        nodeType: 'data_card' | 'status_card';
        updateCadence: 'monthly' | 'quarterly' | 'weekly' | null;
      }[] = [];

      for (const node of nodes) {
        const currentPath = parentPath ? `${parentPath} / ${node.name}` : node.name;

        // Add data + status cards that are not linked
        if ((node.type === 'data_card' || node.type === 'status_card') && !node.linkedNodeId) {
          result.push({
            id: node.id,
            name: node.name,
            path: parentPath || '', // Only show parent path, not including the card itself
            dataValueType: node.suffix === '%' ? 'percentage' : 'number',
            currentValue: node.value,
            nodeType: node.type,
            updateCadence: node.updateCadence
          });
        }

        // Recursively process children (for group cards)
        if (node.children && node.children.length > 0) {
          result.push(...collectDataCards(node.children, currentPath));
        }
      }

      return result;
    };

    return collectDataCards(children);
  }, [children]);

  const showChildCardActions =
    sortedChildren.length > 0 ||
    (canEditData && bulkUpdateDataCards.length > 0) ||
    (canShare && !accessOverviewEnabled);

  // Handle error states and missing dashboard
  if (!dashboardId) {
    return wrapShell(
      <>
        <div className="text-center py-20">
          <p className="text-sm text-content-subtle">Dashboard not found.</p>
        </div>
        <Button variant="primary" onClick={() => navigate('/reporting')}>
          Return to Dashboards
        </Button>
      </>,
      'Dashboard | CompliMind'
    );
  }

  if (error) {
    if (isAccessDenied) {
      return (
        <UnauthorizedPage
          message="You don't have access to this dashboard. If you believe you should have access, please contact the resource owner or your administrator."
          buttons={
            <Button variant="primary" onClick={() => navigate('/reporting')}>
              Return to Dashboards
            </Button>
          }
        />
      );
    }

    return wrapShell(
      <div className="text-center py-20">
        <p className="text-misc-error text-center mb-4">Failed to load data. Please try again.</p>
        <Button variant="primary" icon={ArrowClockwiseIcon} onClick={() => refetch()}>
          Retry
        </Button>
      </div>,
      'Dashboard | CompliMind'
    );
  }

  return wrapShell(
    <>
      <div
        className={`space-y-8 pb-4${useChartTiles ? ` w-full${shell === 'plain' ? '' : ' mx-auto max-w-7xl'}` : ''}`}
      >
        {shell === 'plain' &&
          (Boolean(parentPath) || pagerShowReportingHome || intermediatePath.length > 0 || !isRootView) && (
            <div className={DASHBOARD_TOUR_TARGETS.pager}>
              <ReportingPager
                current={isRootView ? 'scope' : 'group'}
                scope={{
                  name: dashboardName,
                  path: pathForNode(undefined, preservePeriodOnRoot),
                  id: dashboardId
                }}
                intermediatePath={intermediatePath}
                group={
                  !isRootView
                    ? {
                        name: groupName,
                        path: pathForNode(groupId),
                        id: groupId,
                        depth: groupDepth
                      }
                    : undefined
                }
                parentPath={parentPath}
                onDropCard={canEdit ? handleDropCard : undefined}
                isDragging={!!draggingNodeId}
                showReportingHome={pagerShowReportingHome}
              />
            </div>
          )}

        {isLoading ? (
          <Loading />
        ) : (
          <>
            {/* Stale data banner — shown when auto-resolved period is not the current month */}
            {showStaleDataBanner && calcData && (
              <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <WarningIcon size={18} weight="fill" className="shrink-0 text-amber-500" />
                <span>
                  Showing{' '}
                  <strong>
                    {MONTH_NAMES_FULL[calcData.period_month - 1]} {calcData.period_year}
                  </strong>{' '}
                  — the most recent month with data.
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    const now = new Date();
                    setShowStaleDataBanner(false);
                    setReportingState({
                      year: now.getFullYear(),
                      month: now.getMonth() + 1,
                      week: null,
                      grain: 'month'
                    });
                  }}
                  className="ml-auto shrink-0"
                >
                  Go to {MONTH_NAMES_FULL[new Date().getMonth()]}
                </Button>
              </div>
            )}

            {/* View context — period, trend window, colour key. Card fill/sort sit under the hero. */}
            <div className="flex flex-col gap-3 w-full min-w-0">
              <div className="flex min-h-[44px] items-center gap-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <div className={`${DASHBOARD_TOUR_TARGETS.periodSelector} shrink-0`}>
                    <PeriodPicker
                      grain={selectedGrain}
                      year={selectedYear}
                      month={selectedMonth}
                      week={selectedWeek}
                      onChange={(next) => {
                        if (next.grain !== selectedGrain) {
                          posthog?.capture('assurance_period_grain_changed', {
                            dashboard_id: dashboardId,
                            group_id: isRootView ? undefined : groupId,
                            from_grain: selectedGrain,
                            to_grain: next.grain,
                            source: 'dashboard'
                          });
                        } else if (next.grain === 'week') {
                          posthog?.capture('assurance_weekly_period_changed', {
                            dashboard_id: dashboardId,
                            group_id: isRootView ? undefined : groupId,
                            period_year: next.year,
                            period_month: next.month,
                            period_week: next.week,
                            source: 'dashboard'
                          });
                        }
                        setShowStaleDataBanner(false);
                        setReportingState(next);
                      }}
                    />
                  </div>

                  <div className={`${DASHBOARD_TOUR_TARGETS.trendToggle} shrink-0`}>
                    <SegmentedControl
                      size="md"
                      options={TREND_WINDOW_OPTIONS}
                      value={trendWindow === 6 ? '6' : '3'}
                      ariaLabel="Last 3 or last 6 periods"
                      onChange={(value) => setTrendWindow(value === '6' ? 6 : 3)}
                    />
                  </div>

                  {!useChartTiles && (
                    <button
                      type="button"
                      className={`${DASHBOARD_TOUR_TARGETS.complimindInsight} inline-flex items-center gap-1.5 text-content-subtle hover:text-content-default transition-colors shrink-0`}
                      onClick={() => {
                        const newState = !showSummary;
                        if (newState) {
                          posthog?.capture('assurance_insight_expanded', {
                            dashboard_id: dashboardId,
                            node_id: currentNodeId
                          });
                        }
                        setShowSummary(newState);
                      }}
                      aria-expanded={showSummary}
                      aria-label={showSummary ? 'Collapse insights' : 'Expand insights'}
                    >
                      {showSummary ? <CaretDownIcon size={16} /> : <CaretRightIcon size={16} />}
                      <SparkleIcon size={16} weight="fill" className="text-secondary-blue shrink-0" />
                      <span className="text-sm font-medium whitespace-nowrap">CompliMind Insight</span>
                    </button>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {canShare && accessOverviewEnabled && (
                    <Button
                      title={
                        isRootView
                          ? 'See who has access to every card on this dashboard'
                          : 'See who has access to every card in this group'
                      }
                      variant="outline"
                      icon={UsersThreeIcon}
                      onClick={() => {
                        posthog?.capture('assurance_access_overview_opened', {
                          dashboard_id: dashboardId,
                          group_id: isRootView ? undefined : groupId
                        });
                        setShowAccessOverview(true);
                      }}
                    >
                      Access
                    </Button>
                  )}
                  <span
                    ref={legendAnchorRef}
                    className={`${DASHBOARD_TOUR_TARGETS.assuranceLegend} inline-flex shrink-0`}
                  >
                    <Button
                      variant="outline"
                      icon={KeyIcon}
                      aria-label="Assurance colour key"
                      aria-expanded={legendOpen}
                      onClick={() => setLegendOpen((open) => !open)}
                    />
                  </span>
                  <Popover
                    open={legendOpen}
                    anchorEl={legendAnchorRef.current}
                    onClose={() => setLegendOpen(false)}
                    placement="bottom-end"
                    offset={[0, 8]}
                    className="p-3 w-64"
                  >
                    <p className="text-xs font-semibold text-content-default mb-2">Assurance levels</p>
                    <ul className="space-y-1.5">
                      {ASSURANCE_LEGEND_LEVELS.map((level) => (
                        <li key={level} className="flex items-center gap-2 text-sm text-content-default">
                          <span
                            className="h-3.5 w-3.5 shrink-0 rounded-sm border border-border-subtle"
                            style={{ backgroundColor: dashboardAssuranceColors(useChartTiles)[level] }}
                            aria-hidden
                          />
                          {level}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-secondary-blue hover:underline"
                      onClick={() => {
                        setLegendOpen(false);
                        setShowAssuranceInfoModal(true);
                      }}
                    >
                      <InfoIcon size={14} />
                      Learn more
                    </button>
                    {/* Deliberately low-key: the classic layout is an opt-out, not a promoted choice. */}
                    <div className="mt-3 flex items-center justify-between gap-2 border-t border-border-subtle pt-2">
                      <span className="text-xs text-content-subtle">Card style</span>
                      <SegmentedControl
                        size="sm"
                        options={TILE_STYLE_OPTIONS}
                        value={tileStyle}
                        ariaLabel="Dashboard card style"
                        onChange={(next) => {
                          posthog?.capture('assurance_tile_style_changed', {
                            dashboard_id: dashboardId,
                            group_id: isRootView ? undefined : groupId,
                            from_style: tileStyle,
                            to_style: next,
                            // Person property, so you can count who is currently on each layout.
                            $set: { dashboard_tile_style: next }
                          });
                          setTileStyle(next);
                        }}
                      />
                    </div>
                  </Popover>
                </div>
              </div>

              {!useChartTiles && showSummary && (
                <div className="w-full bg-surface border border-border-default rounded-lg p-4 min-w-0">
                  {insightBody}
                </div>
              )}
            </div>

            {/* Hero tile for current group */}
            {currentGroup && (
              <div className={DASHBOARD_TOUR_TARGETS.heroTile}>
                <MetricTile
                  title={`${groupName} - Overall Assurance`}
                  value={currentGroup.value}
                  assurance={currentGroup.assurance}
                  trend={currentGroup.trend}
                  sparkline={currentGroup.sparkline}
                  suffix={currentGroup.suffix}
                  hero
                  isGroupCard={true}
                  aggregationType={currentGroup.aggregationType}
                  previousAssuranceLevel={currentGroup.previousAssuranceLevel}
                  historicalAssuranceLevels={currentGroup.historicalAssuranceLevels}
                  updateCadence={currentGroup.updateCadence}
                  chartSpec={currentGroup.chartSpec}
                  heroInsight={insightBody}
                  onShowInsight={() => {
                    posthog?.capture('assurance_insight_expanded', {
                      dashboard_id: dashboardId,
                      node_id: currentNodeId
                    });
                    setShowInsightModal(true);
                  }}
                  groupNumericDisplayValue={currentGroup.groupNumericDisplayValue}
                  groupNumericDisplayTooltip={currentGroup.groupNumericDisplayTooltip}
                  onOpenDetails={
                    useChartTiles
                      ? () => {
                          posthog?.capture('assurance_node_edit_opened', {
                            dashboard_id: dashboardId,
                            node_id: currentNodeId,
                            node_type: 'group_card',
                            is_root: isRootView
                          });
                          handleOpenDetails(currentNodeId, 'group_card');
                        }
                      : undefined
                  }
                  onComment={
                    useChartTiles
                      ? undefined
                      : (e) =>
                          handleOpenComments(currentNodeId, groupName, currentGroup.type, currentGroup.updateCadence, e)
                  }
                  commentCount={commentCounts[currentNodeId] ?? 0}
                  onEdit={
                    !useChartTiles && canEdit
                      ? () => {
                          posthog?.capture('assurance_node_edit_opened', {
                            dashboard_id: dashboardId,
                            node_id: currentNodeId,
                            node_type: 'group_card',
                            is_root: isRootView
                          });
                          setEditingNodeId(currentNodeId);
                        }
                      : undefined
                  }
                />
              </div>
            )}

            {/* Container so the tile grid sizes to the dashboard, not the viewport: the
                docked details pane narrows the dashboard without changing the viewport. */}
            <div className="@container space-y-4">
              {showChildCardActions && (
                <div className="flex min-h-[44px] items-center gap-2">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    {sortedChildren.length > 0 && (
                      <Dropdown
                        options={METRIC_TILE_SORT_OPTIONS}
                        initialValue={sortBy}
                        onChange={(value) => setSortBy(value as MetricTileSortOption)}
                        prefix="Sort:"
                        size="md"
                        width="w-44"
                        ariaLabel="Sort tiles"
                        className="shrink-0"
                      />
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {canEditData && bulkUpdateDataCards.length > 0 && (
                      <Button
                        title="Bulk update data values for this group"
                        variant="primary"
                        size="md"
                        icon={CalendarCheckIcon}
                        onClick={() => {
                          posthog?.capture('assurance_bulk_update_opened', {
                            dashboard_id: dashboardId,
                            group_id: currentNodeId,
                            data_cards_count: bulkUpdateDataCards.length,
                            period_grain: selectedGrain,
                            weekly_cards_count: bulkUpdateDataCards.filter((c) => c.updateCadence === 'weekly').length,
                            monthly_cards_count: bulkUpdateDataCards.filter(
                              (c) => (c.updateCadence ?? 'monthly') === 'monthly'
                            ).length,
                            quarterly_cards_count: bulkUpdateDataCards.filter((c) => c.updateCadence === 'quarterly')
                              .length
                          });
                          setShowBulkUpdateModal(true);
                        }}
                        className="whitespace-nowrap"
                      >
                        Run {monthNames[selectedMonth - 1]} update
                      </Button>
                    )}
                    {canShare && !accessOverviewEnabled && (
                      <Dropdown
                        className="inline-flex shrink-0 items-center leading-none"
                        triggerClassName="inline-flex items-center leading-none"
                        trigger={<Button variant="outline" icon={DotsThreeIcon} aria-label="More dashboard actions" />}
                        options={[{ value: 'share', label: 'Share', icon: <ShareIcon size={16} /> }]}
                        value=""
                        onChange={(value) => {
                          if (value === 'share') {
                            handleOpenShare();
                          }
                        }}
                        placement="bottom-end"
                        menuWidth={220}
                      />
                    )}
                  </div>
                </div>
              )}

              {sortedChildren.length === 0 && (
                <div className="flex flex-col gap-5 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-5 @[640px]:flex-row @[640px]:items-start @[640px]:justify-between">
                  <div className="min-w-0 flex-1">
                    {canEdit ? (
                      <>
                        <p className="font-medium text-gray-800">This {isRootView ? 'dashboard' : 'group'} is empty</p>
                        <p className="text-sm text-gray-600 mt-1">
                          Not sure where to start? Chat can help you fill this {isRootView ? 'dashboard' : 'group'}. You
                          can also add cards with the “+” tile, or upload a spreadsheet in chat if you have one.
                        </p>
                        <DashboardChatExampleButtons
                          className="mt-3"
                          examples={dashboardPopulateChatExamples(populateChatScope)}
                          onSelect={handlePopulateWithChat}
                        />
                      </>
                    ) : (
                      <>
                        <p className="font-medium text-gray-800">No metrics yet</p>
                        <p className="text-sm text-gray-600 mt-1">
                          This {isRootView ? 'dashboard' : 'group'} has no metrics yet. Someone with edit access can add
                          them.
                        </p>
                      </>
                    )}
                  </div>
                  {canEdit && <DashboardCreateLinks className="shrink-0 @[640px]:max-w-xs" />}
                </div>
              )}

              {/* Child nodes grid */}
              <div
                className={`grid grid-cols-1 @[540px]:grid-cols-2 @[880px]:grid-cols-3 gap-4 items-stretch ${DASHBOARD_TOUR_TARGETS.metricsGrid}`}
              >
                {sortedChildren.map((child) => (
                  <MetricTile
                    key={child.id}
                    title={child.name}
                    value={child.value}
                    assurance={child.assurance}
                    trend={child.trend}
                    sparkline={child.sparkline}
                    suffix={child.suffix}
                    isGroupCard={child.type === 'group_card'}
                    isStatusCard={child.type === 'status_card'}
                    isLinked={!!child.linkedNodeId}
                    aggregationType={child.aggregationType}
                    hasLinkedCardAccess={child.hasLinkedCardAccess ?? true}
                    previousAssuranceLevel={child.previousAssuranceLevel}
                    historicalAssuranceLevels={child.historicalAssuranceLevels}
                    updateCadence={child.updateCadence}
                    chartSpec={child.chartSpec}
                    draggable={canEdit && !child.linkedNodeId}
                    nodeId={child.id}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    isDropZone={canEdit && child.type === 'group_card' && !child.linkedNodeId && !!draggingNodeId}
                    onDrop={handleDropCard}
                    isDragOver={dragOverGroupId === child.id}
                    showCantDropFeedback={
                      canEdit &&
                      !!draggingNodeId &&
                      ((child.type === 'group_card' && !!child.linkedNodeId) ||
                        child.type === 'data_card' ||
                        child.type === 'status_card')
                    }
                    cantDropReason={
                      canEdit && !!draggingNodeId && child.type === 'group_card' && child.linkedNodeId
                        ? 'linked'
                        : canEdit && !!draggingNodeId && (child.type === 'data_card' || child.type === 'status_card')
                          ? 'data-card'
                          : undefined
                    }
                    onDragEnter={
                      canEdit && !!draggingNodeId && child.type === 'group_card' && child.linkedNodeId
                        ? () => handleDragOverLinkedCard(child.id)
                        : canEdit && !!draggingNodeId && child.type === 'group_card' && !child.linkedNodeId
                          ? handleDragOverGroup
                          : canEdit && !!draggingNodeId && (child.type === 'data_card' || child.type === 'status_card')
                            ? () => handleDragOverDataCard(child.id)
                            : undefined
                    }
                    onDragLeave={
                      canEdit && !!draggingNodeId && child.type === 'group_card' && child.linkedNodeId
                        ? handleDragLeaveLinkedCard
                        : canEdit && !!draggingNodeId && child.type === 'group_card' && !child.linkedNodeId
                          ? handleDragLeaveGroup
                          : canEdit && !!draggingNodeId && (child.type === 'data_card' || child.type === 'status_card')
                            ? handleDragLeaveDataCard
                            : undefined
                    }
                    onDrilldown={
                      child.type === 'group_card'
                        ? () => {
                            // For linked group cards, navigate to the original source (only if user has access)
                            if (child.linkedNodeId && child.linkedNodeDashboardId) {
                              if (child.hasLinkedCardAccess === false) {
                                // User doesn't have access - show a toast message
                                toast.error("You don't have access to view the original card");
                                return;
                              }
                              posthog?.capture('assurance_linked_group_drilldown', {
                                dashboard_id: dashboardId,
                                linked_node_id: child.linkedNodeId,
                                linked_dashboard_id: child.linkedNodeDashboardId
                              });
                              // linkedNodeDashboardId is a permission-checked entry
                              // point, not necessarily the linked group's ultimate
                              // root dashboard - when it's the linked node itself,
                              // open it directly as a root view rather than nesting
                              // it under itself via /group/.
                              navigate(
                                buildPath(
                                  child.linkedNodeDashboardId === child.linkedNodeId
                                    ? `/reporting/dashboard/${child.linkedNodeDashboardId}`
                                    : `/reporting/dashboard/${child.linkedNodeDashboardId}/group/${child.linkedNodeId}`
                                )
                              );
                            } else {
                              // Regular group card - drill down within current dashboard
                              posthog?.capture('assurance_group_drilldown', {
                                dashboard_id: dashboardId,
                                group_id: child.id,
                                group_name: child.name
                              });
                              navigate(pathForNode(child.id));
                            }
                          }
                        : undefined
                    }
                    onOpenDetails={
                      useChartTiles
                        ? () => {
                            posthog?.capture('assurance_node_edit_opened', {
                              dashboard_id: dashboardId,
                              node_id: child.id,
                              node_type: child.type
                            });
                            handleOpenDetails(child.id, child.type);
                          }
                        : undefined
                    }
                    onComment={
                      useChartTiles
                        ? undefined
                        : (e) => handleOpenComments(child.id, child.name, child.type, child.updateCadence, e)
                    }
                    commentCount={commentCounts[child.id] ?? 0}
                    onEdit={
                      !useChartTiles &&
                      !child.linkedNodeId &&
                      (canEdit || (canEditData && (child.type === 'data_card' || child.type === 'status_card')))
                        ? () => {
                            posthog?.capture('assurance_node_edit_opened', {
                              dashboard_id: dashboardId,
                              node_id: child.id,
                              node_type: child.type
                            });
                            setEditingNodeId(child.id);
                          }
                        : undefined
                    }
                    onGoToOriginal={
                      // Only show "Go to Original" button for linked DATA cards
                      // Linked group cards navigate to original on click, so button is redundant
                      child.linkedNodeId && child.linkedNodeDashboardId && child.type !== 'group_card'
                        ? () => {
                            posthog?.capture('assurance_linked_card_go_to_original', {
                              dashboard_id: dashboardId,
                              linked_node_id: child.linkedNodeId,
                              linked_dashboard_id: child.linkedNodeDashboardId
                            });
                            // Navigate to dashboard and open modal for the original data card
                            navigate(
                              `/reporting/dashboard/${child.linkedNodeDashboardId}?openNode=${child.linkedNodeId}`
                            );
                          }
                        : undefined
                    }
                    onUnlink={
                      child.linkedNodeId && canEdit
                        ? () => setUnlinkingNode({ id: child.id, name: child.name })
                        : undefined
                    }
                    groupNumericDisplayValue={child.groupNumericDisplayValue}
                    groupNumericDisplayTooltip={child.groupNumericDisplayTooltip}
                    isPinned={child.isPinned}
                    onTogglePin={isOwner ? () => handleTogglePin(child.id, child.isPinned) : undefined}
                  />
                ))}

                {/* Add child card - only for users who can edit */}
                {canEdit && (
                  <div
                    className={`${DASHBOARD_TOUR_TARGETS.addMetricCard} min-w-0 ${
                      useChartTiles ? DASHBOARD_CHART_TILE_HEIGHT_CLASS : DASHBOARD_CLASSIC_TILE_HEIGHT_CLASS
                    }`}
                  >
                    <AddNodeCard
                      parentId={currentNodeId}
                      excludeDashboardId={dashboardId}
                      onCreate={handleCreateNode}
                      label="Add Metric"
                      className="h-full w-full justify-center max-w-none md:max-w-none"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Card details pane (chart tiles) */}
      {useChartTiles &&
        details &&
        (() => {
          const allProcessed = calcData ? calcData.children.map(processNodeForDisplay) : [];
          const detailsNode =
            findNodeById(allProcessed, details.nodeId) ?? (details.nodeId === currentNodeId ? currentGroup : null);
          const detailsNodeChildren =
            detailsNode?.children?.map((c) => ({
              id: c.id,
              title: c.name,
              type: c.type,
              groupNumericDisplayValue: c.groupNumericDisplayValue ?? null
            })) ?? [];
          const siblingIds = sortedChildren
            .filter((c) => c.type === 'data_card' || c.type === 'status_card')
            .map((c) => c.id);
          return (
            <CardDetailsPanel
              key={`${details.nodeId}:${details.settingsTab ?? ''}`}
              open={!!details}
              onClose={handleCloseDetails}
              nodeId={details.nodeId}
              scopeId={dashboardId}
              nodeName={detailsNode?.name ?? 'Card'}
              nodeType={detailsNode?.type ?? 'data_card'}
              updateCadence={detailsNode?.updateCadence ?? null}
              currentValue={detailsNode?.value ?? null}
              assurance={detailsNode?.assurance ?? 'NaN'}
              suffix={detailsNode?.suffix ?? ''}
              year={selectedYear}
              month={selectedMonth}
              week={displayWeek}
              grain={selectedGrain}
              canEdit={canEdit && !detailsNode?.linkedNodeId}
              canEditData={canEditData && !detailsNode?.linkedNodeId}
              canComment={canComment}
              initialTab={details.tab}
              initialSettingsTab={details.settingsTab}
              groupChildren={detailsNodeChildren}
              siblingIds={siblingIds}
              onDeleted={handleNodeDeleted}
              onSaved={() => {
                refetch();
              }}
              onOpenNode={(nodeId, tab, settingsTab) => {
                setDetails({ nodeId, tab: tab ?? details.tab, settingsTab });
              }}
              onOpenDashboardReminders={openDashboardReminderSettings}
              docked={detailsDocked}
            />
          );
        })()}

      {/* Classic edit modal */}
      {!useChartTiles &&
        editingNodeId &&
        (() => {
          const allProcessed = calcData ? calcData.children.map(processNodeForDisplay) : [];
          const editingNode =
            findNodeById(allProcessed, editingNodeId) ?? (editingNodeId === currentNodeId ? currentGroup : null);
          const editingNodeChildren =
            editingNode?.children?.map((c) => ({
              id: c.id,
              title: c.name,
              type: c.type,
              groupNumericDisplayValue: c.groupNumericDisplayValue ?? null
            })) ?? [];
          return (
            <NodeDetailModal
              nodeId={editingNodeId}
              scopeId={dashboardId}
              isOpen={!!editingNodeId}
              initialTabLabel={editingSettingsTab}
              viewingPeriod={
                calcData
                  ? {
                      period_year: calcData.period_year,
                      period_month: calcData.period_month,
                      period_week: selectedGrain === 'week' ? (displayWeek ?? null) : null
                    }
                  : { period_year: selectedYear, period_month: selectedMonth, period_week: displayWeek ?? null }
              }
              onClose={() => {
                setEditingNodeId(null);
                setEditingSettingsTab(undefined);
                refetch();
              }}
              onDeleted={handleNodeDeleted}
              readOnly={!canEditData}
              dataEntryOnly={canEditData && !canEdit}
              groupChildren={editingNodeChildren}
              onOpenDashboardReminders={editingNodeId !== dashboardId ? openDashboardReminderSettings : undefined}
            />
          );
        })()}

      {/* Dashboard ShareIcon Modal (root level) */}
      <DashboardSharing
        dashboardId={dashboardId}
        dashboardName={dashboardName}
        userEmail={user?.email}
        organisationName={user?.getActiveTenant(pathname)?.name || undefined}
        isOwner={isOwner}
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        existingShares={existingShares}
        existingGroupShares={existingGroupShares}
        onRemoveGroupShare={handleRemoveGroupShare}
        tenantShare={tenantShare}
        inheritedTenantPermission={inheritedTenantPermission}
        userPermission={userPermission}
        onAddPeople={handleAddPeople}
        onRemovePerson={handleRemovePerson}
        onUpdatePermission={handleUpdatePermission}
        onShareWithOrganisation={handleShareWithTenant}
        onUpdateOrganisationPermission={handleUpdateTenantShare}
        onRemoveOrganisationShare={handleRemoveTenantShare}
      />

      {/* Group ShareIcon Modal (nested group level) */}
      {groupId && (
        <DashboardSharing
          dashboardId={groupId}
          dashboardName={currentGroup?.name || 'Group'}
          userEmail={user?.email}
          organisationName={user?.getActiveTenant(pathname)?.name || undefined}
          isOwner={isOwner}
          open={showGroupShareModal}
          onClose={() => setShowGroupShareModal(false)}
          existingShares={groupSharesList}
          existingGroupShares={groupExistingGroupShares}
          onRemoveGroupShare={handleGroupRemoveGroupShare}
          tenantShare={groupTenantShare}
          inheritedTenantPermission={groupInheritedTenantPermission}
          userPermission={userPermission}
          onAddPeople={handleGroupAddPeople}
          onRemovePerson={handleGroupRemovePerson}
          onUpdatePermission={handleGroupUpdatePermission}
          onShareWithOrganisation={handleGroupShareWithTenant}
          onUpdateOrganisationPermission={handleGroupUpdateTenantShare}
          onRemoveOrganisationShare={handleGroupRemoveTenantShare}
        />
      )}

      <Modal
        open={showInsightModal}
        onClose={() => setShowInsightModal(false)}
        title={
          <span className="inline-flex items-center gap-2">
            <SparkleIcon size={18} weight="fill" className="text-secondary-blue" />
            CompliMind Insight
          </span>
        }
        aria-label="CompliMind Insight"
        className="w-full max-w-2xl"
        cta={
          <DashboardChatExampleButtons
            className="w-full"
            examples={dashboardInsightChatExamples(populateChatScope)}
            onSelect={handleInsightChat}
          />
        }
      >
        {insightBody}
      </Modal>

      {/* Assurance Levels InfoIcon Modal */}
      <AssuranceLevelsInfoModal open={showAssuranceInfoModal} onClose={() => setShowAssuranceInfoModal(false)} />

      {/* Bulk Data Update Modal */}
      <BulkDataUpdateModal
        open={showBulkUpdateModal}
        onClose={() => setShowBulkUpdateModal(false)}
        nodeId={currentGroup?.id || dashboardId}
        nodeName={currentGroup?.name || 'Dashboard'}
        dataCards={bulkUpdateDataCards}
        initialYear={selectedYear}
        initialMonth={selectedMonth}
        initialWeek={displayWeek}
        initialGrain={selectedGrain}
        onSuccess={() => {
          // Refetch the dashboard data after bulk update
          refetch();
        }}
      />

      {/* Unlink Confirmation Modal */}
      <ConfirmModal
        open={!!unlinkingNode}
        onClose={() => setUnlinkingNode(null)}
        onConfirm={handleUnlinkConfirm}
        title="Remove Linked Card"
        message={`Are you sure you want to remove "${unlinkingNode?.name}" from this dashboard? This will only remove the link - the original card will not be affected.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="warning"
        isLoading={deleteNodeMutation.isPending}
      />

      {/* Access overview — scoped to whichever node the user is on, so a nested
          group shows that group and its cards, never the dashboard above. */}
      {accessOverviewEnabled && (
        <AccessOverviewModal
          open={showAccessOverview}
          onClose={() => setShowAccessOverview(false)}
          nodeId={isRootView ? dashboardId : (groupId as string)}
          nodeName={isRootView ? (dashboardInfo?.title ?? 'This dashboard') : groupName}
          onManageCard={(card) => {
            // The tree is a navigator: editing happens in the card's own share
            // panel, which already implements the floor rules. Already on that
            // card → open the panel here. Otherwise go there and ask it to open.
            setShowAccessOverview(false);
            if (card.id === dashboardId) {
              setShowShareModal(true);
            } else if (card.id === groupId) {
              setShowGroupShareModal(true);
            } else {
              navigate(buildPath(`/reporting/dashboard/${dashboardId}/group/${card.id}`), {
                state: { openShareModal: true }
              });
            }
          }}
        />
      )}

      {/* Classic comment popup */}
      {!useChartTiles && commentNodeId && (
        <NodeCommentPopup
          nodeId={commentNodeId}
          nodeName={commentNodeName}
          scopeId={commentNodeId}
          open={!!commentNodeId}
          anchorEl={commentAnchorEl}
          onClose={handleCloseComments}
          canComment={canComment}
          year={selectedYear}
          month={selectedMonth}
          week={displayWeek}
          grain={selectedGrain}
          nodeType={commentNodeType ?? undefined}
          updateCadence={commentNodeCadence}
        />
      )}

      {/* Dashboard Tour - only shown on root dashboard view */}
      {isRootView && (
        <Joyride
          steps={dashboardTourSteps}
          run={runTour}
          continuous={true}
          showProgress={true}
          showSkipButton={true}
          callback={handleTourCallbackWrapper}
          styles={dashboardTourStyles}
          locale={dashboardTourLocale}
          tooltipComponent={JoyrideTooltip}
        />
      )}
    </>
  );
}
